/**
 * Drives the `router.events` emitter from React Router's location.
 *
 * Next fires routeChangeStart before a navigation and routeChangeComplete after it. A
 * client-side React Router navigation is synchronous — there is no payload to fetch and
 * therefore no gap between the two — so both fire from the same effect, start first, on
 * every location change after the first.
 *
 * The first location is skipped: it is the initial load, not a route CHANGE, and firing
 * routeChangeComplete for it would make AuthGuard's one-shot token strip run against a
 * URL the middleware has already cleaned.
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { routerEvents } from './router-events';

export default function RouterEventsBridge() {
  const location = useLocation();
  const previous = useRef<string | null>(null);

  useEffect(() => {
    const url = `${location.pathname}${location.search}${location.hash}`;

    if (previous.current === null) {
      previous.current = url;
      return;
    }
    if (previous.current === url) return;

    previous.current = url;
    routerEvents.emit('routeChangeStart', url);
    routerEvents.emit('routeChangeComplete', url);
  }, [location.pathname, location.search, location.hash]);

  return null;
}
